import { ApplicationMimeTypesEnum } from "../enums/ApplicationMimeTypesEnum";

export function getAttachmentUrl(base64Data: string | undefined, url: string | undefined): string {
    return base64Data
        ? `data:image/jpeg;base64,${base64Data}`
        : url
            ? url
            : "";
}

export function getApplicationMimeType(file: Blob): ApplicationMimeTypesEnum {
    switch (file?.type) {
        case 'text/html':
            return ApplicationMimeTypesEnum.HTMLText;

        case 'application/vnd.ms-excel':
            return ApplicationMimeTypesEnum.MSExcel;

        case 'application/msword':
            return ApplicationMimeTypesEnum.MSWord;

        case 'application/pdf':
            return ApplicationMimeTypesEnum.PDF;

        case 'application/xml':
        case 'text/xml':
            return ApplicationMimeTypesEnum.XMLText;

        case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
            return ApplicationMimeTypesEnum.MSWord2007;

        case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
            return ApplicationMimeTypesEnum.MSExcel2007;

        case 'image/gif':
            return ApplicationMimeTypesEnum.GIF;

        case 'image/png':
            return ApplicationMimeTypesEnum.PNG;

        case 'image/jpeg':
            return ApplicationMimeTypesEnum.JPEG;

        case 'text/plain':
            return ApplicationMimeTypesEnum.TXT;

        case 'image/bmp':
            return ApplicationMimeTypesEnum.Bitmap;

        // case '???':
        //     return ApplicationMimeTypesEnum.DYN;

        case 'application/json':
            return ApplicationMimeTypesEnum.JSON;

        default:
            return ApplicationMimeTypesEnum.Binary;
    }
}

export function fileToBase64(file: File | Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = async () => {
            const base64String = (reader.result as string)?.split(',')?.pop() ?? "";
            resolve(base64String);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);        
    })
}

export function getFilename(fileName: string) {
    return fileName?.split(/[\\/]/)?.pop();
}

export function getFilenameWithoutExtension(fileName: string) {
    const basename = fileName?.split(/[\\/]/)?.pop();
    const pos = basename?.lastIndexOf('.') ?? -1;

    return (basename === "" || pos < 1)
        ? ""
        : basename?.substring(0, pos);
}

export function getFileExtension(fileName: string) {
    const basename = fileName?.split(/[\\/]/)?.pop();
    const pos = basename?.lastIndexOf('.') ?? -1;

    return (basename === "" || pos < 1)
        ? ""
        : basename?.slice(pos + 1);
}

export type MimeTypeMagicNumber = {
    mime: string,
    extension?: string,
    pattern: (number | undefined)[]
}

const MimeTypeMagicNumbers: Array<MimeTypeMagicNumber> = [
    {
        mime: 'application/octet-stream',
        extension: 'shp',
        // from https://www.esri.com/content/dam/esrisites/sitecore-archive/Files/Pdfs/library/whitepapers/pdfs/shapefile.pdf
        // Bytes 0-3 contain the value 9994, or 0x0000270a.
        pattern: [0x00, 0x00, 0x27, 0x0a]
    },
    {
        mime: 'application/text',
        extension: 'prj',
        // NOTE: This pattern is essentially just the text "PROJCS[".
        //  .prj files are just text files, but they should start 
        //  with this specific text string      
        pattern: [0x50, 0x52, 0x4f, 0x4a, 0x43, 0x53, 0x5b]        
    },
    {
        mime: 'application/text',
        extension: 'prj',
        // NOTE: This pattern is essentially just the text "GEOGCS[".
        //  .prj files are just text files, but they should start 
        //  with this specific text string      
        pattern: [0x47, 0x45, 0x4f, 0x47, 0x43, 0x53, 0x5b]        
    },
    {
        mime: 'application/vnd.google-earth.kmz',
        extension: 'kmz',
        pattern: [0x50, 0x4b, 0x03, 0x04]
        //pattern: [0x50, 0x4b, 0x05, 0x06] // Empty archive
        //pattern: [0x50, 0x4b, 0x07, 0x08] // Spanned archive
    },
]

function isMimeType(bytes: Uint8Array, mimeType: MimeTypeMagicNumber): boolean {
    return mimeType.pattern.every((p, i) => !p || bytes[i] === p);
}

function readFileHeader(file: File | Blob): Promise<Uint8Array> {
    const maxBytesNeeded = Math.max(...MimeTypeMagicNumbers.map(m => m.pattern.length));
    const header = file.slice(0, maxBytesNeeded);

    return new Promise((resolve, reject) => {
        let reader = new FileReader();
        reader.onloadend = (e) => {
            if (!e || !reader.result) return;

            resolve(new Uint8Array(reader.result as ArrayBuffer));
        }
        reader.onerror = reject;
        reader.readAsArrayBuffer(header);
    })
}

export async function validateMimeType(file: File | Blob, fileName: string): Promise<boolean> {    
    const header = await readFileHeader(file);
    const ext = getFileExtension(fileName)?.toLowerCase();
    const magicNumbers = MimeTypeMagicNumbers.filter(o => o.extension === ext);
    const valid = magicNumbers.length === 0 || magicNumbers.some(o => isMimeType(header, o));
    return Promise.resolve(valid);
}